// ============================================================================
// JOB EXPENSES TAB
// Allows adding and viewing expenses linked to a job/task
// Uses same ChildEntryForm pattern from SmartTask for expense recording
// Expenses flow to ExpenseInbox for approval
// Date: February 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {Plus, Receipt, Camera,
  Calendar, Store, Eye, Trash2, ShoppingBag,
  Sparkles, CheckCircle2, X} from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { notify, confirm } from '@/lib/notifications';
import AppointmentReferenceBadge from '@/components/work-items/AppointmentReferenceBadge';
import { isCapacitor } from '@/lib/platformUtils';
import { getApiUrl } from '@/lib/apiConfig';
import ChildEntryForm, { type ChildEntryFormData } from '@/app/client-zone/modules/total-operations/components/smart-task/components/ChildEntryForm';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// TYPES
// ============================================================================

interface JobExpense {
  id: string;
  item_number?: string;
  title: string;
  description?: string;
  grand_total: number;
  amount?: number;
  status: string;
  created_at: string;
  item_type?: string;
  expense_category?: string;
  parent_appointment_id?: string;
  parent_appointment_number?: string;
  vendor_name?: string;
  vehicle_registration?: string;
  receipt_photos?: string[];
  custom_fields?: {
    parent_appointment_id?: string;
    parent_appointment_number?: string;
    purchase_type?: string;
    vendor_name?: string;
    payment_method?: string;
    receipt_number?: string;
    receipt_photos?: string[];
    notes?: string;
    ocr_line_items?: Array<{ description: string; quantity?: number; total?: number }>;
    recorded_by_name?: string;
    source?: string;
  };
}

interface JobExpensesTabProps {
  workItemId: string;
  workItem: { id: string; item_number?: string; title?: string };
  isActive: boolean;
  readOnly?: boolean;
  openComposerToken?: number;
  onExpensesChange?: (count: number) => void;
}

const EXPENSE_ENTRY_ICONS: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  purchase: { icon: ShoppingBag, color: '#ec4899', label: 'Purchase' },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const safeAreaTop = 'max(env(safe-area-inset-top, 0px), var(--safe-area-top, 0px))';
const safeAreaBottom = 'max(env(safe-area-inset-bottom, 0px), var(--safe-area-bottom, 0px))';

// ============================================================================
// COMPONENT
// ============================================================================

export default function JobExpensesTab({ workItemId, workItem, isActive, readOnly = false, openComposerToken, onExpensesChange }: JobExpensesTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const isCapacitorApp = isCapacitor();
  
  // State
  const [expenses, setExpenses] = useState<JobExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showChildEntryForm, setShowChildEntryForm] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState<string | null>(null);
  const hasLoaded = useRef(false);

  // ============================================================================
  // DERIVED STATE
  // ============================================================================

  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.grand_total || e.amount || 0), 0);
  const approvalReadyCount = expenses.filter((expense) => ['completed', 'pending'].includes(String(expense.status || '').toLowerCase())).length;
  const approvedCount = expenses.filter((expense) => String(expense.status || '').toLowerCase() === 'approved').length;
  const receiptCoverageCount = expenses.filter((expense) => (expense.custom_fields?.receipt_photos?.length || expense.receipt_photos?.length || 0) > 0).length;

  // ============================================================================
  // LOAD EXPENSES
  // ============================================================================
  
  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch('/api/v1/work-items/fleet', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getJobExpenses',
          jobId: workItemId,
          include_related: true
        })
      });

      const data = await response.json();
      if (data.success) {
        const expenseList = data.expenses || data.data || [];
        setExpenses(expenseList);
        onExpensesChange?.(expenseList.length);
      }
    } catch (err) {
      console.error('Error loading expenses:', err);
    } finally {
      setLoading(false);
    }
  }, [onExpensesChange, workItemId]);

  useEffect(() => {
    if (isActive && !hasLoaded.current) {
      void loadExpenses();
      hasLoaded.current = true;
    }
  }, [isActive, loadExpenses]);

  useEffect(() => {
    if (readOnly && showChildEntryForm) {
      setShowChildEntryForm(false);
    }
  }, [readOnly, showChildEntryForm]);

  useEffect(() => {
    if (!isActive || readOnly || !openComposerToken) {
      return;
    }

    setShowChildEntryForm(true);
  }, [isActive, openComposerToken, readOnly]);

  // ============================================================================
  // CHILD ENTRY FORM SUBMIT (delegates to /api/v1/work-items/child-entries)
  // ============================================================================

  const handleChildEntrySubmit = useCallback(async (entry: ChildEntryFormData) => {
    setShowChildEntryForm(false);

    // If ChildEntryForm already persisted the entry as a finance document,
    // just refresh the expense list
    if (entry.already_persisted) {
      loadExpenses();
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        notify.error('Not authenticated');
        return;
      }

      const payload = {
        parent_work_item_id: entry.work_item_id || workItemId,
        entry_type: entry.type,
        amount: entry.amount,
        notes: entry.notes,
        metadata: {
          ...(entry.metadata || {}),
          source: 'portal',
          source_module: 'billing_dashboard',
        },
        photos: entry.photos || [],
        location_lat: entry.location?.latitude,
        location_lng: entry.location?.longitude,
        location_accuracy: entry.location?.accuracy,
      };

      const res = await fetch(getApiUrl('/api/v1/work-items/child-entries'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error('[JobExpensesTab] Failed to save child entry:', errText);
        notify.error('Failed to save expense — please try again');
        return;
      }

      notify.success('Expense saved successfully');
      loadExpenses();
    } catch (err) {
      console.error('[JobExpensesTab] Error saving child entry:', err);
      notify.error('Failed to save expense');
    }
  }, [loadExpenses, workItemId]);

  // ============================================================================
  // DELETE EXPENSE
  // ============================================================================

  const deleteExpense = async (expenseId: string) => {
    if (readOnly) {
      return;
    }

    if (!(await confirm({ message: 'Delete this expense?', variant: 'danger' }))) return;

    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch('/api/v1/work-items', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: [expenseId] })
      });

      if (response.ok) {
        notify.success('Expense deleted');
        loadExpenses();
      }
    } catch (err) {
      console.error('Error deleting expense:', err);
      notify.error('Failed to delete expense');
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    if (status === 'approved') return '\u2713 Approved';
    if (status === 'rejected') return '\u2717 Rejected';
    if (status === 'completed') return '\u23F3 Pending Approval';
    return '\u23F3 Pending';
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-tour-id="work-item-expenses-tab">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className={`rounded-full px-3 py-1.5 text-[11px] font-medium ${readOnly ? (isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 border border-gray-200') : (isDark ? 'bg-emerald-950/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')}`}>
          {readOnly ? 'View mode only' : 'Expense capture enabled'}
        </span>
        {!readOnly && (
          <button
            onClick={() => setShowChildEntryForm(true)}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[13px] font-medium text-white transition-colors"
            style={{ backgroundColor: currentTheme.colors.primary }}
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className={`rounded-xl border px-4 py-3 ${isDark ? 'border-gray-800 bg-gray-950/60' : 'border-gray-200 bg-gray-50/80'}`}>
            <div className="flex items-center gap-2">
              <div className={`rounded-lg p-2 ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                <Receipt className="h-4 w-4 text-emerald-500" />
              </div>
              <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Captured</span>
            </div>
            <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{expenses.length}</p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Expense items linked to this workspace</p>
          </div>
          <div className={`rounded-xl border px-4 py-3 ${isDark ? 'border-emerald-900/50 bg-emerald-950/20' : 'border-emerald-200 bg-emerald-50/80'}`}>
            <div className="flex items-center gap-2">
              <div className={`rounded-lg p-2 ${isDark ? 'bg-emerald-950/60 text-emerald-200' : 'bg-white text-emerald-700 border border-emerald-200'}`}>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Approval ready</span>
            </div>
            <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{approvalReadyCount}</p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Waiting for board review or already queued</p>
          </div>
          <div className={`rounded-xl border px-4 py-3 ${isDark ? 'border-blue-900/50 bg-blue-950/20' : 'border-blue-200 bg-blue-50/80'}`}>
            <div className="flex items-center gap-2">
              <div className={`rounded-lg p-2 ${isDark ? 'bg-blue-950/60 text-blue-200' : 'bg-white text-blue-700 border border-blue-200'}`}>
                <Camera className="h-4 w-4 text-blue-500" />
              </div>
              <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Receipt cover</span>
            </div>
            <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{receiptCoverageCount}</p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Entries with attached evidence</p>
          </div>
          <div className={`rounded-xl border px-4 py-3 ${isDark ? 'border-amber-900/50 bg-amber-950/20' : 'border-amber-200 bg-amber-50/80'}`}>
            <div className="flex items-center gap-2">
              <div className={`rounded-lg p-2 ${isDark ? 'bg-amber-950/60 text-amber-200' : 'bg-white text-amber-700 border border-amber-200'}`}>
                <Sparkles className="h-4 w-4 text-amber-500" />
              </div>
              <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Recoverable spend</span>
            </div>
            <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>R {totalExpenses.toFixed(2)}</p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{approvedCount} already approved</p>
          </div>
      </div>

      {/* Info Banner */}
      <div className={`rounded-2xl border px-4 py-3 text-[13px] ${isDark ? 'border-blue-900/70 bg-blue-950/30' : 'border-blue-200 bg-blue-50/60'}`}>
        <p className={isDark ? 'text-blue-300' : 'text-blue-700'}>
          Expenses added here flow into the <strong>Expense Approval Board</strong> for manager sign-off before they are converted into client-facing finance documents or internal recovery.
        </p>
      </div>

      {/* Expenses List */}
      {expenses.length === 0 ? (
        <div className={`text-center py-12 rounded-2xl border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
          <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No expenses recorded for this job yet.
          </p>
          {!readOnly && (
            <button
              onClick={() => setShowChildEntryForm(true)}
              className="mt-3 text-sm font-medium"
              style={{ color: currentTheme.colors.primary }}
            >
              + Add first expense
            </button>
          )}
        </div>
      ) : (
        <div className={`overflow-hidden rounded-2xl border shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <div className={`hidden grid-cols-[minmax(0,1.8fr)_minmax(120px,0.8fr)_120px] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] md:grid ${isDark ? 'border-b border-gray-800 bg-gray-900 text-gray-500' : 'border-b border-gray-200 bg-gray-50 text-gray-500'}`}>
            <span>Expense</span>
            <span>Status</span>
            <span className="text-right">Amount</span>
          </div>
          {expenses.map((expense, index) => {
            const expCat = expense.custom_fields?.purchase_type || expense.expense_category || expense.item_type || 'purchase';
            const ec = EXPENSE_ENTRY_ICONS[expCat] || { icon: Receipt, color: '#f59e0b', label: String(expCat).charAt(0).toUpperCase() + String(expCat).slice(1) };
            const EIcon = ec.icon;
            const parentAppointmentId = expense.parent_appointment_id || expense.custom_fields?.parent_appointment_id || null;
            const parentAppointmentNumber = expense.parent_appointment_number || expense.custom_fields?.parent_appointment_number || null;
            const receiptCount = expense.custom_fields?.receipt_photos?.length || expense.receipt_photos?.length || 0;

            return (
              <div 
                key={expense.id}
                className={`px-4 py-3 ${index > 0 ? isDark ? 'border-t border-gray-800' : 'border-t border-gray-200' : ''} ${isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50/70'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Type icon */}
                    <div className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: ec.color + '15' }}>
                      <EIcon className="w-4 h-4" style={{ color: ec.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className={`text-[13px] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {expense.title || expense.description || ec.label}
                        </h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(expense.status)}`}>
                          {getStatusLabel(expense.status)}
                        </span>
                        {parentAppointmentNumber && (
                          <AppointmentReferenceBadge
                            appointmentId={parentAppointmentId}
                            appointmentNumber={parentAppointmentNumber}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                            iconClassName="w-3 h-3"
                            title={`Open appointment ${parentAppointmentNumber}`}
                          >
                            <>
                              <span>📋 {parentAppointmentNumber}</span>
                            </>
                          </AppointmentReferenceBadge>
                        )}
                      </div>
                      {(expense.custom_fields?.vendor_name || expense.vendor_name) && (
                        <p className={`mt-1 flex items-center gap-1 text-[13px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          <Store className="w-3.5 h-3.5" />
                          {expense.custom_fields?.vendor_name || expense.vendor_name}
                        </p>
                      )}
                      {expense.vehicle_registration && (
                        <p className="text-xs text-gray-400 mt-0.5">{expense.vehicle_registration}</p>
                      )}
                      <div className="mt-1.5 flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(expense.created_at).toLocaleDateString()}
                        </span>
                        <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          {ec.label}
                        </span>
                        {expense.custom_fields?.source && (
                          <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            via {expense.custom_fields.source}
                          </span>
                        )}
                        {receiptCount > 0 && (
                          <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            {receiptCount} receipt{receiptCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      Amount
                    </p>
                    <p className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      R {Number(expense.grand_total || expense.amount || 0).toFixed(2)}
                    </p>
                    <div className="mt-1.5 flex items-center justify-end gap-1.5">
                      {receiptCount > 0 ? (
                        <button
                          onClick={() => setViewingReceipt(
                            expense.custom_fields?.receipt_photos?.[0] || expense.receipt_photos?.[0] || null
                          )}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="View Receipt"
                        >
                          <Eye className="w-4 h-4 text-gray-500" />
                        </button>
                      ) : null}
                      {!readOnly && (
                        <button
                          onClick={() => deleteExpense(expense.id)}
                          className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Total */}
          <div className={`p-3 ${isDark ? 'border-t border-gray-800 bg-gray-950/40' : 'border-t border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[13px] font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Total Expenses
              </span>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                R {totalExpenses.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* CHILD ENTRY FORM (job-linked expense capture via ChildEntryForm)   */}
      {/* ================================================================== */}
      <ChildEntryForm
        isOpen={showChildEntryForm}
        entryType="purchase"
        workItemId={workItemId}
        linkableWorkItemId={workItemId}
        linkableWorkItemLabel={workItem.item_number || workItem.title || null}
        linkableWorkItemNumber={workItem.item_number || null}
        linkableWorkItemTitle={workItem.title || null}
        isJobLinkedExpenseOverride={true}
        hideLinkToggle={true}
        titleOverride="Job Linked Expense"
        onSubmit={handleChildEntrySubmit}
        onCancel={() => setShowChildEntryForm(false)}
      />

      {/* Receipt Viewer Modal */}
      {viewingReceipt && typeof document !== 'undefined' && createPortal(
        <div className={`fixed inset-0 isolate z-[120] flex bg-black/80 ${isCapacitorApp ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'}`}>
          <div className="absolute inset-0" onClick={() => setViewingReceipt(null)} />
          <div className={`relative flex items-center justify-center ${isCapacitorApp ? 'w-full h-[100dvh] max-h-[100dvh] px-4' : 'max-w-3xl max-h-[90vh]'}`}>
            <button
              onClick={() => setViewingReceipt(null)}
              className={`absolute z-10 p-2 text-white hover:text-gray-300 ${isCapacitorApp ? 'top-4 right-4' : '-top-10 right-0'}`}
              style={{ top: isCapacitorApp ? `calc(${safeAreaTop} + 0.5rem)` : undefined }}
            >
              <X className="w-6 h-6" />
            </button>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={viewingReceipt}
              alt="Receipt"
              className={`max-w-full object-contain ${isCapacitorApp ? 'max-h-full rounded-none' : 'max-h-[85vh] rounded-lg'}`}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
